import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { stringify } from 'query-string'
import { useRouter } from 'next/router'

import { breadcrumbTransformer } from '../models/Breadcrumb/breadcrumbTransformer'
import { recipesCoverTransformer } from '../models/Recipes'
import { RecipesByTagTemplateProps } from '../templates/RecipesByTag'
import { colors } from '../theme'
import {
  recipesByTagListTransformer,
  recipesByTagSeoTransformer,
} from '../models/RecipesByTag'
import { selectors } from '../redux'
import {
  actions as RecipesActions,
  RecipesByTagState,
} from '../redux/recipesbytag/redux'
import { SettingsState } from '../redux/settings/redux'
import Router from '../routes/router'
import routes from '../routes/definitions'
import { tagCommanderTransformer } from '../models/TagCommander/tagCommanderTransformer'
import { TcPageTemplateNames } from '../configuration/tagcommander'

import usePWAInstallPrompt from './usePWAInstallPrompt'

const useRecipesByTag = () => {
  usePWAInstallPrompt()
  const recipes: RecipesByTagState['list'] = useSelector(
    selectors.recipesByTag.list
  )
  const settings: SettingsState['list'] = useSelector(selectors.settings.list)
  const tagName: RecipesByTagState['tag'] = useSelector(
    selectors.recipesByTag.tag
  )
  const currentPage = useSelector(selectors.recipesByTag.page)
  const hasPreviousPages = useSelector(selectors.recipesByTag.hasPreviousPages)
  const hasMorePages = useSelector(selectors.recipesByTag.hasMorePages)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const resultsRef = useRef<HTMLDivElement>(null)
  const nextRouter = useRouter()

  const loadMoreHandler = useCallback(() => {
    dispatch(RecipesActions?.loadMoreRecipesPage())
  }, [dispatch])
  const loadPreviousHandler = useCallback(() => {
    dispatch(RecipesActions?.loadPreviousRecipesPage())
  }, [dispatch])

  useEffect(() => {
    const params = stringify(
      {
        page: currentPage,
      },
      { arrayFormat: 'comma', skipNull: true, skipEmptyString: true }
    )

    Router.replaceRoute(
      `${Router.getRouteUrl(routes.recipesByTag.name, {
        tag: tagName,
      })}?${params}`,
      {
        shallow: true,
        scroll: false,
      }
    )
  }, [currentPage, tagName])

  const breadcrumbName = t('breadcrumb_recipes_by_tag')

  const breadcrumbConfig = breadcrumbTransformer({
    bgColor: colors.greenMint,
    hasFamItem: false,
    t,
    lastItems: [
      {
        text: breadcrumbName,
      },
    ],
  })

  const currentTcTemplate = useMemo(
    () =>
      nextRouter.query?.query
        ? TcPageTemplateNames.RecipeSearch
        : TcPageTemplateNames.Recipe,
    [nextRouter.query?.query]
  )

  const recipesTemplateProps: RecipesByTagTemplateProps = useMemo(
    () => ({
      coverSectionProps: recipesCoverTransformer({
        t,
        breadcrumbItems: breadcrumbConfig,
        backgroundImageDesktop: settings?.covers?.recipes_list?.desktop ?? '',
        backgroundImageMobile: settings?.covers?.recipes_list?.mobile ?? '',
        backgroundImageAlt: settings?.covers?.recipes_list?.alt ?? '',
        initialValue: '',
        tag: tagName,
      }),
      seoProps: recipesByTagSeoTransformer(t, tagName),
      recipesListSectionProps: recipesByTagListTransformer({
        t,
        recipes: recipes ?? [],
        loadMoreFunction: hasMorePages ? loadMoreHandler : undefined,
        loadPreviousFunction: hasPreviousPages
          ? loadPreviousHandler
          : undefined,
        tcVars: {
          chapter2: t('breadcrumb_home'),
          chapter3: t('breadcrumb_eat_better'),
        },
        resultsRef,
      }),
      tcProps: tagCommanderTransformer({
        pageName: breadcrumbName,
        tcTemplate: currentTcTemplate,
        tcCAt1: t('breadcrumb_home'),
        tcCAt2: t('breadcrumb_eat_better'),
      }),
    }),
    [
      breadcrumbConfig,
      breadcrumbName,
      currentTcTemplate,
      hasMorePages,
      hasPreviousPages,
      loadMoreHandler,
      loadPreviousHandler,
      recipes,
      settings?.covers?.recipes_list?.alt,
      settings?.covers?.recipes_list?.desktop,
      settings?.covers?.recipes_list?.mobile,
      t,
      tagName,
    ]
  )

  return {
    recipesTemplateProps,
  }
}

export default useRecipesByTag
